/**
 * @fileoverview System-web module catalog service — local-first reads/writes
 *               for the platform `modules` collection.
 * @module system-web/services/moduleService
 *
 * Responsibilities:
 * - Read the synced `modules` Dexie table (listModules, getModule).
 * - Normalize catalog docs into selector options (listModuleOptions).
 * - Write module CRUD + JSON seed imports to Dexie with sync wakeup.
 *
 * Dependencies:
 * - @firesquirrel/local-db (getSystemDb, wakeLocalWrite)
 * - @firesquirrel/core-domain (module code derivation + toModuleOptions)
 * - ./systemAuditLogService (audit trail for module mutations)
 *
 * Architecture:
 * - Local-first: all reads/writes hit Dexie; background sync reconciles with
 *   the system DB `modules` collection (the source of truth).
 * - `listModuleOptions` is the single entrypoint for module SELECTORS
 *   (lookup categories, lookup values). Catalog management surfaces use
 *   `listModules` directly.
 *
 * Notes:
 * - Module codes are name-derived lowerCamelCase — the canonical contract in
 *   Docs/MODULE-CODE-CONTRACT.md. Writes are strict (registry codes only);
 *   reads tolerate drifted kebab spellings via the shared alias table.
 * - Legacy cleanup (sanitizeModuleDoc, isLegacyRemovedModule,
 *   dedupeModulesByCanonicalCode, isRetiredLocalDeletion) keeps pre-catalog
 *   and drifted-spelling records from surfacing as extra modules in readers.
 *
 * @localFirst
 * Source of Truth: local (system DB Dexie modules table).
 * Write Flow: Persist accepted changes locally, then wake the existing sync process.
 * Sync Strategy: Server changes reconcile back into Dexie; catalog reads remain pure.
 * Conflict Resolution: Existing canonical codes are immutable and sync conflicts remain server-owned.
 *
 * @errorHandling
 * Strategy: Reject non-canonical catalog identities before writing Dexie.
 * User Impact: Invalid create/import actions fail without local persistence or sync wakeup.
 * Logging: Mutation audit failures are warned; validation failures are returned to the caller.
 */

import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';
import {
  canonicalizeTenantModuleCode,
  isKnownModuleCode,
  moduleCodeFromName,
  normalizeModuleName,
  toModuleOptions,
  type ModuleOption,
} from '@firesquirrel/core-domain';
import { writeAuditLog } from './systemAuditLogService';

/**
 * @security
 * Validation: Restricts local catalog writes to canonical registry codes.
 * Trust Boundary: Validate Dexie mutation and import payloads before persistence.
 */
function assertKnownModuleCode(code: unknown): asserts code is string {
  if (!isKnownModuleCode(code)) {
    throw new Error(`Module code "${String(code ?? '')}" is not a canonical module code`);
  }
}

/**
 * Derives a module's code from its name using the shared lowerCamelCase
 * convention (Docs/MODULE-CODE-CONTRACT.md §1). Kept as a named export because
 * ModulesPage renders the generated code in the editor.
 */
export function generateModuleCode(name: string): string {
  return moduleCodeFromName(normalizeModuleName(name));
}

function sanitizeModuleDoc(doc: any): any {
  if (!doc) return doc;

  const { minTier, version, dependencies, icon, sortOrder, ...rest } = doc;
  return {
    ...rest,
    code: String(rest.code ?? '').trim(),
    name: normalizeModuleName(rest.name),
    description: String(rest.description ?? '').trim() || undefined,
    category: rest.category ?? 'inspection',
    status: rest.status ?? 'alpha',
    enabledByDefault: Boolean(rest.enabledByDefault ?? false),
  };
}

function normalizeModuleInput(data: any, existing?: any) {
  const now = new Date().toISOString();
  const name = normalizeModuleName(data?.name ?? existing?.name);
  // Codes are name-derived at creation and immutable afterward — a rename is
  // a migration, not an edit. For new docs the input code is never trusted.
  const code = existing ? String(existing.code ?? '').trim() : generateModuleCode(name);
  assertKnownModuleCode(code);

  return {
    _id: data?._id ?? existing?._id ?? `local-${Date.now()}`,
    code,
    name,
    description: String(data?.description ?? existing?.description ?? '').trim() || undefined,
    category: data?.category ?? existing?.category ?? 'inspection',
    status: data?.status ?? existing?.status ?? 'alpha',
    enabledByDefault: Boolean(data?.enabledByDefault ?? existing?.enabledByDefault ?? false),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
}

function hasLegacyModuleFields(doc: any): boolean {
  return ['minTier', 'version', 'dependencies', 'icon', 'sortOrder']
    .some((field) => Object.prototype.hasOwnProperty.call(doc ?? {}, field));
}

export function isLegacyRemovedModule(doc: any): boolean {
  const code = String(doc?.code ?? '').trim().toLowerCase();
  const name = String(doc?.name ?? '').trim().toLowerCase();
  // 'fire-code' is intentionally absent — it is a drifted spelling that still
  // maps to the live 'fireCodeCompliance' module, so it survives reads and
  // collapses via dedupeModulesByCanonicalCode. Only pre-catalog spellings
  // that never map to a live module are hidden outright.
  return code === 'firecode' || name === 'fire code';
}

/**
 * Retired local-deletion scheme: docs removed client-side before the
 * `_deleted: true` tombstone contract existed were instead flagged
 * `isDeleted: true` and had their code renamed to `<code>__deleted_<id>`.
 * The push engine ignores `isDeleted`, so these records still sync and must
 * be filtered out of every catalog read.
 */
export function isRetiredLocalDeletion(doc: any): boolean {
  return doc?.isDeleted === true || String(doc?.code ?? '').includes('__deleted_');
}

/**
 * Collapse catalog docs that canonicalize to the same module code — drifted
 * environments can carry both kebab (`wildland`) and canonical camelCase
 * (`wildlandFire`) generations of the same module (see
 * Docs/MODULE-CODE-CONTRACT.md §3). The canonical-spelled doc wins when
 * present; otherwise a server-issued (Mongo ObjectId) doc beats a
 * client-created `local-*` doc. The emitted row keeps the winner's real code
 * so selection always writes a code the server catalog accepts.
 */
export function dedupeModulesByCanonicalCode(docs: any[]): any[] {
  const groups = new Map<string, any[]>();
  for (const doc of docs) {
    const canonical = canonicalizeTenantModuleCode(doc.code) || doc.code;
    const group = groups.get(canonical) ?? [];
    group.push(doc);
    groups.set(canonical, group);
  }
  const results: any[] = [];
  for (const [canonical, group] of groups) {
    // Prefer server-issued catalog documents over unsynced `local-*` documents
    // so tenant selection never submits an unsynced local code when a
    // server-backed alias already exists. ObjectId-shaped ids count as
    // server-issued — a hex-only check misses them and lets the local
    // canonical spelling win.
    const serverDocs = group.filter((d) => isServerIssuedId(d?._id));
    const pool = serverDocs.length > 0 ? serverDocs : group;
    const winner = pool.find((d) => d.code === canonical) ?? pool[0];
    results.push(winner);
  }
  return results;
}

function isServerIssuedId(id: unknown): boolean {
  if (id == null) return false;
  if (typeof id === 'string') {
    return !id.startsWith('local-') && /^[0-9a-f]{24}$/i.test(id);
  }
  if (typeof id !== 'object') return false;
  const objectId = id as { toHexString?: () => unknown; $oid?: unknown };
  if (typeof objectId.toHexString === 'function') {
    return /^[0-9a-f]{24}$/i.test(String(objectId.toHexString()));
  }
  return typeof objectId.$oid === 'string' && /^[0-9a-f]{24}$/i.test(objectId.$oid);
}

function toSeedModuleRecord(doc: any): {
  code: string;
  name: string;
  description?: string;
  category: string;
  status: string;
  enabledByDefault: boolean;
} {
  const sanitized = sanitizeModuleDoc(doc);
  assertKnownModuleCode(sanitized.code);
  return {
    code: sanitized.code,
    name: sanitized.name,
    ...(sanitized.description ? { description: sanitized.description } : {}),
    category: sanitized.category,
    status: sanitized.status,
    enabledByDefault: Boolean(sanitized.enabledByDefault),
  };
}

export function formatModuleSeedJson(modules: any[]): string {
  return JSON.stringify(modules.map((module) => toSeedModuleRecord(module)), null, 2);
}

function parseModuleSeedJson(seedJson: string): ReturnType<typeof toSeedModuleRecord>[] {
  const parsed = JSON.parse(seedJson);
  if (!Array.isArray(parsed)) {
    throw new Error('Seed JSON must be an array of modules');
  }
  return parsed.map((module: any) => {
    if (!module || typeof module !== 'object') {
      throw new Error('Seed JSON contains an invalid module entry');
    }
    const name = normalizeModuleName(module.name);
    const code = String(module.code ?? '').trim() || moduleCodeFromName(name);
    const category = String(module.category ?? '').trim();
    const status = String(module.status ?? '').trim();
    if (!code || !name || !category || !status) {
      throw new Error('Each module must include code, name, category, and status');
    }
    return toSeedModuleRecord({ ...module, code, name });
  });
}

async function replaceLocalModules(modules: any[]): Promise<void> {
  const db = await getSystemDb();
  const current = await db.modules.toArray();
  if (modules.length > 0) {
    for (const doc of modules.map((item) => sanitizeModuleDoc(item))) {
      const existing = (current as any[]).find((item) => String(item?.code ?? '') === doc.code);
      await db.modules.put({
        ...doc,
        _id: existing?._id ?? doc._id ?? `local-${Date.now()}-${doc.code}`,
        createdAt: existing?.createdAt ?? doc.createdAt ?? new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }
  }
  wakeLocalWrite('modules');
}

async function seedModulesPayload(modules: any[]): Promise<number> {
  const payload = modules.map((module) => toSeedModuleRecord(module));

  await replaceLocalModules(payload);
  return payload.length;
}

async function cleanupLegacyModuleDocs(): Promise<void> {
  const db = await getSystemDb();
  const docs = await db.modules.toArray();
  let changed = false;

  for (const doc of docs as any[]) {
    if (!hasLegacyModuleFields(doc)) {
      continue;
    }

    const cleaned = sanitizeModuleDoc(doc);
    if (JSON.stringify(cleaned) !== JSON.stringify(doc)) {
      await db.modules.put(cleaned);
      changed = true;
    }
  }

  if (changed) {
    wakeLocalWrite('modules');
  }
}

export async function listModules(params?: { status?: string; search?: string }): Promise<any[]> {
  const db = await getSystemDb();
  await cleanupLegacyModuleDocs();
  const docs = await db.modules.toArray();
  let results = (docs as any[])
    .map((doc) => sanitizeModuleDoc(doc))
    .filter((doc) => !isLegacyRemovedModule(doc) && !isRetiredLocalDeletion(doc));
  results = dedupeModulesByCanonicalCode(results);
  if (params?.status) results = results.filter(m => m.status === params.status);
  if (params?.search) {
    const q = params.search.toLowerCase();
    results = results.filter(m => m.code?.toLowerCase().includes(q) || m.name?.toLowerCase().includes(q));
  }
  return results;
}

/**
 * Selector options for the real modules catalog — the single source for any
 * UI where a module is selectable (lookup categories, lookup values).
 * Returns `{ key: code, label: name }` options via the shared
 * `toModuleOptions` normalizer so this list can never drift from the catalog.
 */
export async function listModuleOptions(): Promise<ModuleOption[]> {
  return toModuleOptions(await listModules());
}

export async function getModule(codeOrId: string): Promise<any | null> {
  const db = await getSystemDb();
  let doc = await db.modules.get(codeOrId);
  if (!doc) {
    const all = await listModules();
    const match = all.find((m: any) => m.code === codeOrId);
    return match ?? null;
  }
  const cleaned = sanitizeModuleDoc(doc);
  if (JSON.stringify(cleaned) !== JSON.stringify(doc)) {
    await db.modules.put(cleaned);
    wakeLocalWrite('modules');
  }
  return cleaned;
}

export async function upsertModule(data: any): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.modules.get(data._id);
  const isNew = !existing;
  const next = normalizeModuleInput(data, existing);
  await db.modules.put(next);
  wakeLocalWrite('modules');
  writeAuditLog({
    collection: 'modules',
    documentId: next._id,
    action: isNew ? 'create' : 'update',
    summary: isNew ? `Created module "${next.code}"` : `Updated module "${next.code}"`,
  }).catch((err) => {
    console.warn('[moduleService] Failed to write audit log for module save:', err);
  });
}

export async function deleteModule(id: string): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.modules.get(id);
  const code = existing ? (existing as any).code ?? id : id;

  if (existing) {
    await db.modules.put({
      ...(existing as any),
      _deleted: true,
      deletedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    wakeLocalWrite('modules');
  }

  writeAuditLog({
    collection: 'modules',
    documentId: id,
    action: 'delete',
    summary: `Deleted module "${code}"`,
  }).catch((err) => {
    console.warn('[moduleService] Failed to write audit log for module delete:', err);
  });
}

export async function seedModulesFromCurrentCatalog(): Promise<number> {
  const db = await getSystemDb();
  const current = await db.modules.toArray();
  return seedModulesPayload(current);
}

export async function seedModulesFromJson(seedJson: string): Promise<number> {
  const modules = parseModuleSeedJson(seedJson);
  return seedModulesPayload(modules);
}
